/**
 * Excel Template Engine
 *
 * Opens an .xlsx template via ExcelJS, finds placeholder cells,
 * and injects artefact content. Uses the existing ExcelJS library
 * already installed in the project.
 *
 * Supported placeholders (in cell values):
 *   TITLE   — document title
 *   DATE    — export date
 *   AUTHOR  — user display name
 *   BRAND   — brand name
 *   CONTENT — artefact content (combined, plain text)
 */

import type { Cell, Row, Workbook, Worksheet } from "exceljs";
import type { Artefact } from "@/types/artefacts";
import type { CapturedImage } from "@/utils/artefactExport/htmlToImage";
import type { TableData } from "@/utils/tableUtils";

import { logger } from "@/telemetry";
import { ArtefactType } from "@/types/artefacts";
import { parseTableContent } from "@/utils/tableUtils";
import { formatLongDate } from "@/utils/dateFormatting";
import { stripMarkdown } from "@/utils/templateEngine/templateUtils";

// Image sizing constants for Excel embedding
const IMAGE_SCALE_DIVISOR = 2; // Captured at 2x scale for quality
const IMAGE_MAX_WIDTH = 1200; // Maximum image width in pixels
const IMAGE_MAX_HEIGHT = 800; // Maximum image height in pixels
const IMAGE_ROW_HEIGHT_APPROX = 20; // Approximate Excel row height in pixels

// Header background colour (ARGB format for ExcelJS)
const EXCEL_HEADER_BG_ARGB = "FF28326E";

// Colour of the notice written in place of a chart that could not be embedded
const EXCEL_NOTICE_ARGB = "FFFF6B00";

// Widest an auto-sized column may grow, in characters
const COLUMN_MAX_WIDTH = 50;

// The data URL prefix stripped from a captured image before ExcelJS embeds it
const IMAGE_DATA_URL_PREFIX = /^data:image\/\w+;base64,/;

// A cell value Excel would read as a formula, which is prefixed to defuse it
const FORMULA_PREFIX = /^[=+\-@]/;

export interface ExcelTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  /** Font family for injected content (from Export Font setting). */
  fontFamily?: string;
  artefacts: Array<{
    title: string;
    content: string;
    type: ArtefactType;
    id?: string;
  }>;
}

/**
 * Prepare template data from artefacts.
 */
export function prepareExcelTemplateData(
  artefacts: Artefact[],
  options: {
    title?: string;
    author?: string;
    brand?: string;
    locale?: string;
    fontFamily?: string;
  } = {},
): ExcelTemplateData {
  return {
    title: options.title || "",
    // The locale parameter had never been passed by any caller, so this silently
    // followed navigator.language and rendered "August 28, 2026" on a US machine.
    // formatLongDate honours a day-first locale and corrects a month-first one.
    date: formatLongDate(new Date(), options.locale) ?? "",
    author: options.author || "",
    brand: options.brand || "",
    fontFamily: options.fontFamily,
    artefacts: artefacts.map((a) => ({
      title: a.title,
      content: a.type === ArtefactType.TABLE ? a.content : stripMarkdown(a.content),
      type: a.type,
      id: a.id,
    })),
  };
}

// Excel sheet name limit
const SHEET_NAME_MAX_LENGTH = 31;

/**
 * Sanitise and deduplicate a worksheet name for Excel (max 31 chars, no special chars).
 */
function sanitiseSheetName(
  title: string,
  workbook: { getWorksheet: (name: string) => unknown },
): string {
  // Remove characters invalid in Excel sheet names: \ / ? * [ ]
  let name = title.replace(/[\\/?*[\]]/g, "").trim() || "Sheet";

  name = name.slice(0, SHEET_NAME_MAX_LENGTH);

  // Ensure unique
  let finalName = name;
  let counter = 1;

  while (workbook.getWorksheet(finalName)) {
    const suffix = ` (${counter})`;

    finalName = name.slice(0, SHEET_NAME_MAX_LENGTH - suffix.length) + suffix;
    counter++;
  }

  return finalName;
}

type TemplateArtefact = ExcelTemplateData["artefacts"][number];

/** An artefact's bold title cell, in the export font when one is set. */
function writeTitleCell(
  worksheet: Worksheet,
  row: number,
  title: string,
  fontFamily: string | undefined,
): void {
  worksheet.getCell(row, 1).value = title;
  worksheet.getCell(row, 1).font = {
    bold: true,
    size: 14,
    ...(fontFamily && { name: fontFamily }),
  };
}

/**
 * Fallback for table content that does not parse: the title, then the raw
 * content in the cell below it. Returns the next available row.
 */
function writeRawTableContent(
  worksheet: Worksheet,
  artefact: TemplateArtefact,
  startRow: number,
  fontFamily: string | undefined,
): number {
  writeTitleCell(worksheet, startRow, artefact.title, fontFamily);

  const contentRow = startRow + 1;

  worksheet.getCell(contentRow, 1).value = artefact.content;
  if (fontFamily) worksheet.getCell(contentRow, 1).font = { name: fontFamily };

  return contentRow + 1;
}

/** The table's header row, white bold text on the brand header colour. */
function writeTableHeaderRow(
  worksheet: Worksheet,
  row: number,
  headers: string[],
  fontFamily: string | undefined,
): void {
  const headerRow = worksheet.getRow(row);

  headerRow.values = headers;
  headerRow.font = {
    bold: true,
    color: { argb: "FFFFFFFF" },
    size: 12,
    ...(fontFamily && { name: fontFamily }),
  };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: EXCEL_HEADER_BG_ARGB },
  };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
}

/** Size each column to its longest value, capped so a long cell cannot dominate the sheet. */
function autoSizeTableColumns(worksheet: Worksheet, table: TableData): void {
  table.headers.forEach((header: string, colIndex: number) => {
    const column = worksheet.getColumn(colIndex + 1);
    const maxLength = Math.max(
      header.length,
      ...table.rows.map((row: Record<string, string>) => (row[header] || "").toString().length),
    );

    column.width = Math.min(maxLength + 2, COLUMN_MAX_WIDTH);
  });
}

/**
 * A parsed table: title row, styled header row, one row per data row, sized
 * columns and an auto-filter over the header and data. Returns the next
 * available row.
 */
function writeParsedTable(
  worksheet: Worksheet,
  title: string,
  table: TableData,
  startRow: number,
  fontFamily: string | undefined,
): number {
  let currentRow = startRow;

  // Title row
  writeTitleCell(worksheet, currentRow, title, fontFamily);
  currentRow++;

  // Header row with styling
  writeTableHeaderRow(worksheet, currentRow, table.headers, fontFamily);
  currentRow++;

  // Data rows
  for (const row of table.rows) {
    const excelRow = worksheet.getRow(currentRow);
    const values = table.headers.map((h) => row[h] || "");

    excelRow.values = values;
    currentRow++;
  }

  autoSizeTableColumns(worksheet, table);

  // Add auto-filter
  worksheet.autoFilter = {
    from: { row: currentRow - table.rows.length - 1, column: 1 },
    to: { row: currentRow - 1, column: table.headers.length },
  };

  return currentRow;
}

/** A table artefact, or its raw content when it does not parse into headers and rows. */
function writeTableArtefact(
  worksheet: Worksheet,
  artefact: TemplateArtefact,
  startRow: number,
  fontFamily: string | undefined,
): number {
  const table = parseTableContent(artefact.content);

  if (!table || !table.headers || !table.rows) {
    return writeRawTableContent(worksheet, artefact, startRow, fontFamily);
  }

  return writeParsedTable(worksheet, artefact.title, table, startRow, fontFamily);
}

/** An italic notice written in place of a chart image. Returns the next available row. */
function writeChartNotice(
  worksheet: Worksheet,
  row: number,
  notice: string,
  fontFamily: string | undefined,
): number {
  worksheet.getCell(row, 1).value = notice;
  worksheet.getCell(row, 1).font = {
    italic: true,
    color: { argb: EXCEL_NOTICE_ARGB },
    ...(fontFamily && { name: fontFamily }),
  };

  return row + 1;
}

/**
 * The image's display size: half its captured size (it was captured at 2x),
 * scaled down to fit the maximum width and then the maximum height.
 */
function fitChartImage(image: CapturedImage): { width: number; height: number } {
  let width = image.width / IMAGE_SCALE_DIVISOR;
  let height = image.height / IMAGE_SCALE_DIVISOR;

  if (width > IMAGE_MAX_WIDTH) {
    const ratio = IMAGE_MAX_WIDTH / width;

    width = IMAGE_MAX_WIDTH;
    height = height * ratio;
  }

  if (height > IMAGE_MAX_HEIGHT) {
    const ratio = IMAGE_MAX_HEIGHT / height;

    height = IMAGE_MAX_HEIGHT;
    width = width * ratio;
  }

  return { width, height };
}

/**
 * Embed a chart image anchored at `row`. Returns the next available row, past
 * the rows the image spans and one row of spacing.
 */
function embedChartImage(
  workbook: Workbook,
  worksheet: Worksheet,
  image: CapturedImage,
  row: number,
): number {
  const base64Data = image.base64.replace(IMAGE_DATA_URL_PREFIX, "");
  const imageId = workbook.addImage({
    base64: base64Data,
    extension: "png",
  });
  const { width, height } = fitChartImage(image);

  worksheet.addImage(imageId, {
    tl: { col: 0, row: row - 1 },
    ext: { width, height },
    editAs: "oneCell",
  });

  const rowsSpanned = Math.ceil(height / IMAGE_ROW_HEIGHT_APPROX);

  return row + rowsSpanned + 1;
}

/**
 * An HTML artefact: its title, then its captured image, or a notice when no
 * image was captured or the image could not be embedded.
 */
function writeHtmlArtefact(
  workbook: Workbook,
  worksheet: Worksheet,
  artefact: TemplateArtefact,
  imageMap: Map<string, CapturedImage> | undefined,
  startRow: number,
  fontFamily: string | undefined,
): number {
  writeTitleCell(worksheet, startRow, artefact.title, fontFamily);

  const currentRow = startRow + 1;
  const image = artefact.id && imageMap ? imageMap.get(artefact.id) : undefined;

  if (!image) {
    return writeChartNotice(
      worksheet,
      currentRow,
      `Chart: ${artefact.title} (image not available)`,
      fontFamily,
    );
  }

  try {
    return embedChartImage(workbook, worksheet, image, currentRow);
  } catch (error) {
    logger.error("Failed to add HTML artefact image to Excel template", {
      error: String(error),
    });

    return writeChartNotice(
      worksheet,
      currentRow,
      `Chart: ${artefact.title} (could not be embedded)`,
      fontFamily,
    );
  }
}

/**
 * Write a single artefact (table or HTML) into a worksheet starting at startRow.
 * Returns the next available row after the content.
 */
function writeArtefactToSheet(
  workbook: Workbook,
  worksheet: Worksheet,
  artefact: TemplateArtefact,
  imageMap?: Map<string, CapturedImage>,
  startRow: number = 1,
  fontFamily?: string,
): number {
  if (artefact.type === ArtefactType.TABLE) {
    return writeTableArtefact(worksheet, artefact, startRow, fontFamily);
  }

  // HTML artefact — embed image if available
  return writeHtmlArtefact(workbook, worksheet, artefact, imageMap, startRow, fontFamily);
}

/**
 * The placeholder replacements, built from the text artefacts only, since tables
 * and HTML get structured output. Each token is matched as a plain token (TITLE),
 * single-brace ({TITLE}) and double-brace ({{TITLE}}), longest first.
 */
function buildReplacements(
  data: ExcelTemplateData,
  textArtefacts: TemplateArtefact[],
): Record<string, string> {
  const contentText = textArtefacts.map((a) => `${a.title}\n${a.content}`).join("\n\n");
  const tokenValues: Record<string, string> = {
    TITLE: data.title || "",
    DATE: data.date || "",
    AUTHOR: data.author || "",
    BRAND: data.brand || "",
    CONTENT: contentText,
  };

  const replacements: Record<string, string> = {};

  for (const [token, value] of Object.entries(tokenValues)) {
    replacements[`{{${token}}}`] = value;
    replacements[`{${token}}`] = value;
    replacements[token] = value;
  }

  return replacements;
}

/** Replace the placeholders in one string cell, defusing anything Excel would run as a formula. */
function replaceCellPlaceholders(cell: Cell, replacements: Record<string, string>): void {
  if (typeof cell.value !== "string") return;

  let value = cell.value;

  for (const [placeholder, replacement] of Object.entries(replacements)) {
    if (value.includes(placeholder)) {
      value = value.replace(placeholder, replacement);
    }
  }

  // Prevent Excel formula injection: prefix suspicious values with a single quote
  cell.value = FORMULA_PREFIX.test(value) ? `'${value}` : value;
}

/** Walk through all cells in all worksheets and replace placeholders. */
function replacePlaceholders(workbook: Workbook, replacements: Record<string, string>): void {
  workbook.eachSheet((worksheet: Worksheet) => {
    worksheet.eachRow((row: Row) => {
      row.eachCell((cell: Cell) => replaceCellPlaceholders(cell, replacements));
    });
  });
}

/**
 * The sheet that collects every content artefact when they share one: the
 * template's "Exported Content" sheet, else its "Content" sheet, else a new
 * "Content" sheet.
 */
function resolveContentSheet(workbook: Workbook): Worksheet {
  return (
    workbook.getWorksheet("Exported Content") ??
    workbook.getWorksheet("Content") ??
    workbook.addWorksheet("Content")
  );
}

/** The first row to write to: after any existing content, leaving one blank row. */
function firstFreeRow(worksheet: Worksheet): number {
  let currentRow = 1;

  worksheet.eachRow((_, rowNumber) => {
    currentRow = rowNumber + 1;
  });

  return currentRow > 1 ? currentRow + 1 : currentRow;
}

/** Create one worksheet per artefact. */
function writeArtefactsToSeparateSheets(
  workbook: Workbook,
  artefacts: TemplateArtefact[],
  imageMap: Map<string, CapturedImage> | undefined,
  fontFamily: string | undefined,
): void {
  for (const artefact of artefacts) {
    const sheetName = sanitiseSheetName(artefact.title, workbook);
    const worksheet = workbook.addWorksheet(sheetName);

    writeArtefactToSheet(workbook, worksheet, artefact, imageMap, 1, fontFamily);
  }
}

/** Write all content into a single worksheet, two rows apart. */
function writeArtefactsToContentSheet(
  workbook: Workbook,
  artefacts: TemplateArtefact[],
  imageMap: Map<string, CapturedImage> | undefined,
  fontFamily: string | undefined,
): void {
  const contentSheet = resolveContentSheet(workbook);
  let currentRow = firstFreeRow(contentSheet);

  for (const artefact of artefacts) {
    currentRow = writeArtefactToSheet(
      workbook,
      contentSheet,
      artefact,
      imageMap,
      currentRow,
      fontFamily,
    );
    currentRow += 2; // Spacing between artefacts
  }
}

/**
 * Apply an Excel template to artefact data.
 *
 * @param templateBuffer - The .xlsx template file as ArrayBuffer
 * @param data - Template data with placeholders to fill
 * @param imageMap - Optional map of captured HTML artefact images
 * @param options - Export options (e.g. useSeparateWorksheets)
 * @returns Blob of the rendered .xlsx file
 */
export async function applyExcelTemplate(
  templateBuffer: ArrayBuffer,
  data: ExcelTemplateData,
  imageMap?: Map<string, CapturedImage>,
  options?: { useSeparateWorksheets?: boolean },
): Promise<Blob> {
  try {
    // Dynamic import to keep ExcelJS out of the main bundle
    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();

    await workbook.xlsx.load(templateBuffer);

    // Separate artefacts by type
    const tableArtefacts = data.artefacts.filter((a) => a.type === ArtefactType.TABLE);
    const htmlArtefacts = data.artefacts.filter((a) => a.type === ArtefactType.HTML);
    const textArtefacts = data.artefacts.filter(
      (a) => a.type !== ArtefactType.TABLE && a.type !== ArtefactType.HTML,
    );

    replacePlaceholders(workbook, buildReplacements(data, textArtefacts));

    // Collect content artefacts (tables + HTML) that need worksheet output
    const contentArtefacts = [...tableArtefacts, ...htmlArtefacts];
    const useSeparateWorksheets = options?.useSeparateWorksheets ?? false;

    if (contentArtefacts.length > 0) {
      if (useSeparateWorksheets) {
        writeArtefactsToSeparateSheets(workbook, contentArtefacts, imageMap, data.fontFamily);
      } else {
        writeArtefactsToContentSheet(workbook, contentArtefacts, imageMap, data.fontFamily);
      }
    }

    // Generate output
    const buffer = await workbook.xlsx.writeBuffer();

    logger.info("Excel template applied successfully", {
      artefactCount: data.artefacts.length,
      title: data.title,
    });

    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  } catch (error) {
    logger.error("Failed to apply Excel template", { error: String(error) });
    throw new Error(
      `Failed to apply Excel template: ${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }
}
